<template>
  <div class="economy-shell">
    <!-- Economy shell container -->
    <div class="economy-shell__container">
      <!-- Loading State -->
      <EconomySkeleton v-if="isLoading || state.isLoadingAgreement" />

      <!-- Content State - Always show layout with right sidebar -->
      <div v-else class="economy-shell__content">
        <!-- Left Side: Invoice List with embedded states -->
        <div class="economy-shell__content--left">
          <!-- Error State -->
          <div v-if="error" class="economy-shell__left-error">
            <div class="error-content">
              <p class="error-message">{{ error }}</p>
              <BaseButton variant="primary" @click="fetchInvoices"
                >Prøv igjen</BaseButton
              >
            </div>
          </div>

          <!-- Invalid Booking Number State -->
          <div
            v-else-if="!hasValidBookingNumber"
            class="economy-shell__left-empty"
          >
            <div class="empty-content">
              <div class="empty-icon">
                <i class="pi pi-file" style="font-size: 2.5rem" />
              </div>
              <p class="empty-message">Kan ikke vise fakturaer</p>
              <p class="empty-subtitle">Mangler gyldig bookingnummer</p>
            </div>
          </div>

          <!-- No Invoices State -->
          <div v-else-if="!invoices.length" class="economy-shell__left-empty">
            <div class="empty-content">
              <div class="empty-icon">
                <i
                  class="pi pi-exclamation-triangle"
                  style="font-size: 3.5rem"
                />
              </div>
              <p class="empty-message">Ingen fakturaer funnet</p>
              <p class="empty-subtitle">
                Det er ingen fakturaer registrert for denne avtalen
              </p>
            </div>
          </div>

          <!-- Invoice List -->
          <div v-else class="economy-shell__invoice-list">
            <base-list>
              <invoice-list-item
                v-for="invoice in sortedInvoices"
                :key="invoice.invoiceNumber"
                :invoice="mapToInvoiceDTO(invoice)"
                :list-item-id="'invoice-' + invoice.invoiceNumber"
                :project-supplier-name="tender.customerOrganization.orgNumber"
                :project-customer-name="tender.customerOrganization.orgNumber"
                :customer-mode="false"
              />
            </base-list>
          </div>
        </div>

        <!-- Right Side: Invoice & Customer Info - Always visible -->
        <div class="economy-shell__content--right">
          <InvoiceRecipient
            :booking-number="bookingNumber || ''"
            :by-pass-really="invoiceRecipientLogic.shouldBypassReally.value"
            :recipient="invoiceRecipientData.recipient"
            :is-loading="invoiceRecipientData.isLoading"
            :error="invoiceRecipientData.error"
          />
          <CustomerContactInfo
            :customer-details="customerDetailsFromAcceptedUser"
            :is-loading="isLoadingCustomerDetails"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, reactive } from "vue";
import type { InvoiceDTO } from "@/custom-types/GeneralTypes";
import { useFinanceApi } from "@/services/api/useFinanceApi";
import { BaseList, InvoiceListItem } from "@really/really-tender-components";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import InvoiceRecipient from "@/components/AgreementDetails/InvoiceRecipient.vue";
import CustomerContactInfo from "@/components/AgreementDetails/CustomerContactInfo.vue";
import EconomySkeleton from "./EconomySkeleton.vue";
import { useInvoiceRecipientLogic } from "@/composables/useInvoiceRecipientLogic";
import { useExtendedBillable } from "@/composables/useExtendedBillable";
import { useCmsStore } from "@/stores/cms";
import { ContentType } from "@/custom-types/CmsContentTypes";
import type {
  Tender,
  ServiceOffer as ServiceOfferType,
  InvoiceRecipient as InvoiceRecipientType,
} from "@/stores/tender/types";
import dayjs from "dayjs";

// ===== TYPES AND INTERFACES =====
interface CustomerDetails {
  displayName: string | null;
  email: string | null;
  mobilePhone: string | null;
}

interface ComponentState {
  isLoadingAgreement: boolean;
  company: InvoiceRecipientType | null;
}

const props = defineProps<{
  tender: Tender;
  bookingNumber: string | null;
  supplierId: number;
  acceptedServiceOffer?: ServiceOfferType | null;
}>();

// ===== COMPOSABLES AND SERVICES =====
const cmsStore = useCmsStore();
const invoiceRecipientLogic = useInvoiceRecipientLogic();
const { getTenderInvoices } = useFinanceApi();

// ===== REACTIVE STATE =====
const state = reactive<ComponentState>({
  isLoadingAgreement: false,
  company: null,
});

const invoices = ref<InvoiceDTO[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

// Transform CMS ServiceType data to the format expected by useExtendedBillable
const transformServiceTypesData = () => {
  const cmsServiceTypes = cmsStore[ContentType.SERVICE_TYPES_CMS];
  if (!cmsServiceTypes) return [];

  return cmsServiceTypes.map((serviceType) => ({
    label: serviceType.label || "",
    serviceCategories: (serviceType.serviceCategories || []).map(
      (category) => ({
        label: category.label || "",
        title: category.title || "",
      }),
    ),
  }));
};

const { initialize, state: extendedBillableState } = useExtendedBillable({
  tender: props.tender,
  supplierId: props.supplierId,
  serviceTypesCms: transformServiceTypesData(),
});

// Computed property to transform acceptedByUser to CustomerDetails format
const customerDetailsFromAcceptedUser = computed((): CustomerDetails | null => {
  const acceptedByUser = extendedBillableState.acceptedByUser;
  if (!acceptedByUser) return null;

  return {
    displayName: acceptedByUser.displayName || null,
    email: acceptedByUser.email || null,
    mobilePhone: acceptedByUser.mobilePhone || null,
  };
});

// Check if we're loading user data from the extended billable composable
const isLoadingCustomerDetails = computed(() => {
  const hasServiceOffer = !!extendedBillableState.acceptedServiceOffer;
  const hasUserData = !!extendedBillableState.acceptedByUser;
  const isLoadingFrameAgreement = extendedBillableState.isLoadingFrameAgreement;

  return isLoadingFrameAgreement || (hasServiceOffer && !hasUserData);
});

// Computed properties for invoice recipient parameters
const invoiceRecipientParams = computed(() => {
  if (!props.tender?.priceRequests?.[0]) {
    return null;
  }

  const priceRequest = props.tender.priceRequests[0];
  return {
    supplierId: props.supplierId,
    priceRequestId: priceRequest.id,
    customerPropertyId: priceRequest.customerPropertyId,
    orgNumber: props.tender.customerOrganization.orgNumber,
  };
});

// Watch for changes and initialize invoice recipient logic
watch(
  () =>
    [
      props.tender?.customerOrganization?.orgNumber,
      props.acceptedServiceOffer?.integrationSource,
      invoiceRecipientParams.value,
    ] as const,
  async ([orgNumber, integrationSource, params]) => {
    if (orgNumber && params) {
      await invoiceRecipientLogic.initialize({
        orgNumber,
        integrationSource: integrationSource || null,
        invoiceRecipientParams: params,
      });
    }
  },
  { immediate: true },
);

// Get invoice recipient data from the logic
const invoiceRecipientData = computed(() => {
  return invoiceRecipientLogic.invoiceRecipientData.value;
});

// Initialize extended billable when service offer is available
watch(
  () => props.acceptedServiceOffer,
  async (newOffer) => {
    if (newOffer) {
      state.isLoadingAgreement = true;
      try {
        await initialize(newOffer);
      } finally {
        state.isLoadingAgreement = false;
      }
    }
  },
  { immediate: true },
);

const hasValidBookingNumber = computed(() => {
  return !!props.bookingNumber && props.bookingNumber.trim() !== "";
});

const mapToInvoiceDTO = (invoice: InvoiceDTO): InvoiceDTO => ({
  id: invoice.id,
  invoiceNumber: invoice.invoiceNumber,
  customerName: invoice.customerName,
  invoiceDate: invoice.invoiceDate,
  paymentDueDate: invoice.paymentDueDate,
  taxInclusiveAmount: invoice.taxInclusiveAmount,
  statusCode: invoice.statusCode,
  restAmount: invoice.restAmount,
  vatTotalsAmount: invoice.vatTotalsAmount,
  taxExclusiveAmount: invoice.taxExclusiveAmount,
  emailAddress: invoice.emailAddress,
  comment: invoice.comment,
  credited: invoice.credited,
  lastPaymentDate: invoice.lastPaymentDate,
  deliveryDate: invoice.deliveryDate,
  ourReference: invoice.ourReference,
  yourReference: invoice.yourReference,
});

const sortedInvoices = computed(() => {
  return [...invoices.value].sort(
    (a, b) => dayjs(b.invoiceDate).valueOf() - dayjs(a.invoiceDate).valueOf(),
  );
});

const fetchInvoices = async () => {
  // Early return if no valid booking number
  if (!hasValidBookingNumber.value) {
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    error.value = null;

    const response = await getTenderInvoices({
      supplierId: props.supplierId,
      // Type assertion here since we've already checked validity
      bookingNumber: props.bookingNumber as string,
    });

    if (!response || !response.data) {
      throw new Error("Ingen fakturaer funnet");
    }

    invoices.value = response.data;
  } catch (err) {
    error.value =
      "Det oppstod en feil ved henting av fakturaer. Vennligst prøv igjen.";
    console.error("Failed to fetch invoices:", err);
  } finally {
    isLoading.value = false;
  }
};

// Watch for changes in bookingNumber and refetch when it becomes valid
watch(
  () => props.bookingNumber,
  (newBookingNumber) => {
    if (newBookingNumber && newBookingNumber.trim() !== "") {
      fetchInvoices();
    } else {
      // Reset state when booking number becomes invalid
      invoices.value = [];
      error.value = null;
    }
  },
);

onMounted(() => {
  // Since the component will only be mounted when the tab is active,
  // we can fetch invoices directly in onMounted if we have a valid booking number
  if (hasValidBookingNumber.value) {
    fetchInvoices();
  }
});
</script>

<style lang="scss" scoped>
.economy-shell {
  padding: 1rem;

  &__container {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-width: 1060px;
    margin: auto;

    @media (max-width: $bp-sm) {
      padding: 1rem;
      gap: 1.5rem;
    }
  }

  &__content {
    display: flex;
    gap: 2rem;
    width: 100%;

    @media (max-width: $bp-md) {
      flex-direction: column;
      gap: 1.5rem;
    }

    &--left {
      flex: 2;
      min-height: 300px;
    }

    &--right {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }
  }

  // Left section error states
  &__left-error {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    padding: 2rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;

    .error-content {
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      color: #dc2626;

      .error-message {
        font-size: 1rem;
        font-weight: 500;
        margin: 0;
      }
    }
  }

  &__left-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    padding: 2rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;

    .empty-content {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      color: #6b7280;

      .empty-icon {
        font-size: 3rem;
        color: #9ca3af;
        margin-bottom: 0.5rem;
      }

      .empty-message {
        font-size: 1.125rem;
        font-weight: 500;
        color: #374151;
        margin: 0;
      }

      .empty-subtitle {
        font-size: 0.875rem;
        color: #6b7280;
        margin: 0;
        max-width: 300px;
        line-height: 1.4;
      }
    }
  }

  // Legacy error states (kept for backward compatibility if needed elsewhere)
  &__loader,
  &__error,
  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    gap: 1rem;
    color: #1d1d1d;
    font-weight: 400;
  }
}
</style>
